import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { marked } from "marked";
import Header from "components/common/Header";
import Sidebar from "components/common/Sidebar";
import useAppState from "context/useAppState";
import { MARKDOWN_VIEWER_CUSTOM_STYLING } from "utils/constant";
import { toast } from "react-toastify";
import { checkProjectStatus, copyImageForAnnotation, getMarkdownContent, reanalyzeSingleClaimElement, updateReanalysisResult, updateVijilizationStatus, updateCitationsForSingleComparison } from "services/project";
import ReAnalyzeModal from "components/common/modals/ReAnalyzeModal";
import loginLogo from "assets/images/login_logo.svg";

declare global {
  interface Window {
    handleImageEditClick: (s3Path: string) => void;
    handleReAnalyzeClick: () => void;
  }
}

interface VijilizeClaimData {
  activeIndex: {
    patentId: string;
    claimIndex: number;
  };
  selectedClaim: any;
  selectedSimilarity: any;
  clickedItemIndex: number;
  isMatchedItem: boolean;
  annotationId?: string;
  storedClaimKey?: string;
  comparisonId?: string;
}

const MarkdownViewer = () => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);
  const [imagesCount, setImagesCount] = useState<number>(0);
  const [loadedImagesCount, setLoadedImagesCount] = useState<number>(0);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isAnnotationLoading, setIsAnnotationLoading] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedMarkdownContentData, setFetchedMarkdownContentData] =
    useState<any>({});
  const [showReAnalyzeModal, setShowReAnalyzeModal] = useState<boolean>(false);
  const [isVijilizing, setIsVijilizing] = useState<boolean>(false);
  const [actualComparisonId, setActualComparisonId] = useState<string>("");

  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id, vijilizeId } = useParams();
  const {
    setAnnotationS3Path,
    originalImagePath,
    markdownContent,
    setImageCoordinates,
    setMarkdownS3Path,
    setMarkdownContent,
    setOriginalImagePath,
    patentImage,
    setPatentImage,
    setFontInfo
  } = useAppState("vijilize");

  useEffect(() => {
    fetchProjectStatus()
  }, [])

  const fetchProjectStatus = async () => {
    try {
      const { data } = await checkProjectStatus(id)
      console.log({ data })
    } catch (error: any) {
      if (error?.statusCode == 404) {
        navigate('/project')
      }
      console.log({ getProjectStatusError: error })
    }
  }

  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
    setIsLoading(true);
    setError(null);
  }, [location.pathname]);

  // Clean up global function when component unmounts
  useEffect(() => {
    return () => {
      // @ts-ignore
      window.handleImageEditClick = undefined;
    };
  }, []);

  // Handle forceRefresh flag from navigation state
  useEffect(() => {
    if (location.state?.forceRefresh) {
      console.log("Force refresh triggered from navigation state");
      setRefreshKey((prev) => prev + 1);
      // Clear the state to prevent re-triggering on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const claimDataStr = sessionStorage.getItem("vijilizeClaimData");

    if (!claimDataStr || !vijilizeId) {
      setIsLoading(false);
      setError("Missing required data to fetch content");
      return;
    }

    let claimData: VijilizeClaimData;
    try {
      claimData = JSON.parse(claimDataStr);
    } catch (e) {
      console.error("Error parsing vijilizeClaimData from sessionStorage", e);
      setIsLoading(false);
      setError("Invalid claim data format");
      return;
    }

    const { activeIndex, isMatchedItem, annotationId, storedClaimKey } = claimData;

    let claimKey = storedClaimKey || `claim${activeIndex.claimIndex + 1}`;

    if (!activeIndex?.patentId) {
      setIsLoading(false);
      setError("Missing patentId in claim data");
      return;
    }

    const params = {
      comparisonId: vijilizeId,
      patentId: activeIndex.patentId,
      // claimKey: `claim${activeIndex.claimIndex + 1}`,
      claimKey,
      isMatchedItem: isMatchedItem,
      annotationId: annotationId || "",
    };

    // Store the actual comparison ID for later use
    setActualComparisonId(vijilizeId || "");

    const fetchMarkdownContent = async () => {
      if (!params.annotationId) {
        if (markdownContent) {
          setFetchedMarkdownContentData({
            markdown_content: markdownContent,
          });
          setIsLoading(false);
          return;
        }
      }

      try {
        const response: any = await getMarkdownContent(
          params.comparisonId,
          params.patentId,
          params.claimKey,
          params.isMatchedItem,
          params.annotationId
        );

        if (response?.success && response?.data) {
          setFetchedMarkdownContentData(response?.data);
          response?.data?.markdownS3Path &&
            setAnnotationS3Path(response?.data?.markdownS3Path);
          response?.data?.markdownS3Path &&
            setMarkdownS3Path(response?.data?.markdownS3Path);
          response?.data?.originalImagePath &&
            setOriginalImagePath(response?.data?.originalImagePath);
          response?.data?.markdownContent &&
            setMarkdownContent(response?.data?.markdownContent);
          response?.data?.imageCoordinates &&
            setImageCoordinates(response?.data?.imageCoordinates);
          response?.data?.patentImage &&
            setPatentImage(response?.data?.patentImage);
          response?.data?.fontInfo &&
            setFontInfo(response?.data?.fontInfo);
          
          // Store the comparison ID if available in response, otherwise use the one from params
          if (response?.data?.comparisonId) {
            setActualComparisonId(response.data.comparisonId);
          } else if (response?.data?.comparison_id) {
            setActualComparisonId(response.data.comparison_id);
          }
        } else {
          setError(response?.data?.message || "No markdown content found");
        }
      } catch (err: any) {
        console.error("Error fetching markdown content:", err);
        setError(
          err.response?.data?.message || "Failed to fetch markdown content"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarkdownContent();
  }, [vijilizeId, refreshKey]);

  useEffect(() => {
    const contentToRender =
      fetchedMarkdownContentData?.markdown_content || markdownContent;

    if (!contentToRender) {
      if (!isLoading && !error) {
        navigate(-1);
      }
      return;
    }

    try {
      const customStyles = MARKDOWN_VIEWER_CUSTOM_STYLING;

      marked.setOptions({});

      window.handleImageEditClick = async () => {
        if (id) {
          try {
            setIsAnnotationLoading(true);
            const buttons = document.querySelectorAll(".edit-image-button");
            buttons.forEach((button) => {
              button.classList.add("loading");
            });

            let selectedTeam: any = JSON.parse(
              localStorage.getItem("selectedTeam") as string
            );
            let team_id: string = selectedTeam.currentTeamId;

            const response = await copyImageForAnnotation(
              fetchedMarkdownContentData?.original_image_path ||
              originalImagePath,
              id,
              team_id
            );

            if (response?.data && response?.data?.annotation_s3_path) {
              const annotationS3Path = response?.data?.annotation_s3_path;

              setAnnotationS3Path(annotationS3Path);
              navigate(`/project/${id}/vijilize/${vijilizeId}/annotation`);
            } else {
              setIsAnnotationLoading(false);
              buttons.forEach((button) => {
                button.classList.remove("loading");
              });
              toast.error("Failed to prepare image for annotation");
            }
          } catch (error) {
            console.error("Error preparing image for annotation:", error);

            setIsAnnotationLoading(false);
            const buttons = document.querySelectorAll(".edit-image-button");
            buttons.forEach((button) => {
              button.classList.remove("loading");
            });

            toast.error("Error preparing image for annotation");
          }
        }
      };

      window.handleReAnalyzeClick = () => {
        setShowReAnalyzeModal(true);
      };

      const spinnerStyles = `
        <style>
          .edit-image-button .loading-spinner {
            display: none;
          }
          .edit-image-button.loading .loading-spinner {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 2px solid rgba(255,255,255,0.3);
            border-radius: 50%;
            border-top-color: white;
            animation: spin 1s ease-in-out infinite;
            margin-left: 4px;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      `;

      if (!document.getElementById("spinner-styles")) {
        const styleEl = document.createElement("style");
        styleEl.id = "spinner-styles";
        styleEl.innerHTML = spinnerStyles
          .replace("<style>", "")
          .replace("</style>", "");
        document.head.appendChild(styleEl);
      }

      marked.use({
        renderer: {
          image(token) {
            const { href, title, text } = token;
            return `<div class="markdown-image-container" style="position: relative; display: inline-block; max-width: 100%;">
              <img src="${href}" alt="${text || ""}" title="${title || ""
              }" class="markdown-image" data-loading="true" style="max-width: 100%; height: auto; width: auto; display: block;" />
              <div style="position: absolute; top: 8px; right: 8px; display: flex; gap: 4px; z-index: 10;">
                <button type="button" onclick="window.handleImageEditClick()" class="edit-image-button" style="background-color: #1751D0; color: white; border: none; border-radius: 4px; padding: 4px 8px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Annotation
                  <span class="loading-spinner"></span>
                </button>
                <button type="button" onclick="window.handleReAnalyzeClick()" class="reanalyze-button" style="background-color: #1751D0; color: white; border: none; border-radius: 4px; padding: 4px 8px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  ReAnalyze
                </button>
              </div>
            </div>`;
          },
        },
      });

      const html = marked.parse(contentToRender);
      setHtmlContent(customStyles + html);

      setImagesLoaded(false);
      setLoadedImagesCount(0);
      setImagesCount(0);
    } catch (error) {
      console.error("Error parsing markdown:", error);
      setHtmlContent("<p>Error parsing content</p>");
      setImagesLoaded(true);
    }
  }, [fetchedMarkdownContentData, navigate, refreshKey, isLoading, error]);

  useEffect(() => {
    if (!htmlContent || !contentRef.current) return;

    const images = contentRef.current.querySelectorAll("img.markdown-image");
    setImagesCount(images.length);

    if (images.length === 0) {
      setImagesLoaded(true);
      return;
    }

    const handleImageLoad = () => {
      setLoadedImagesCount((prevCount) => {
        const newCount = prevCount + 1;
        if (newCount >= images.length) {
          setImagesLoaded(true);
        }
        return newCount;
      });
    };

    const handleImageError = () => {
      handleImageLoad();
    };

    images.forEach((img) => {
      const imgElement = img as HTMLImageElement;

      if (imgElement.complete) {
        handleImageLoad();
      } else {
        imgElement.addEventListener("load", handleImageLoad);
        imgElement.addEventListener("error", handleImageError);
      }
    });

    return () => {
      images.forEach((img) => {
        const imgElement = img as HTMLImageElement;
        imgElement.removeEventListener("load", handleImageLoad);
        imgElement.removeEventListener("error", handleImageError);
      });
    };
  }, [htmlContent]);

  return (
    <>
      <div className="dashboard-wrapper flex">
        <Sidebar />
        <div className="w-[calc(100%_-_72px)] ml-auto">
          <Header title="Vijilize" count={0} isChildEle={true} type="project" />
          <div className="pt-4 px-4 h-[calc(100vh_-_69px)] overflow-y-auto scrollbar-adjust bg-[#f9fafb]">
            <div className="bg-white rounded-lg shadow-customShadow5 p-8 max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-xl font-interSemiBold text-[#1A202C]">
                  Detailed Analysis
                </h1>
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center text-sm text-[#1751D0] font-interMedium hover:underline"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Back
                </button>
              </div>

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : error ? (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
                role="alert"
              >
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
            ) : !imagesLoaded && imagesCount > 0 ? (
              <div className="animate-pulse">
                <div className="h-7 bg-[#F2F4F7] rounded-md w-3/4 mb-5"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-full mb-3"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-5/6 mb-3"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-4/6 mb-5"></div>
                <div className="h-72 bg-[#F2F4F7] rounded-md w-full mb-5 border border-[#E5E7EB]"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-full mb-3"></div>
                <div className="h-5 bg-[#F2F4F7] rounded-md w-5/6 mb-3"></div>
                <div className="h-8 bg-[#F2F4F7] rounded-md w-3/5 mb-4 mt-8"></div>
                <div className="h-40 bg-[#F2F4F7] rounded-md w-full mb-5 border border-[#E5E7EB]"></div>
              </div>
            ) : (
              <>
                <div
                  key={refreshKey}
                  ref={contentRef}
                  className="markdown-content prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />

                {/* Patent Image Section */}
                {patentImage && patentImage.patent_figure_path && (
                  <div className="mt-8 border-t pt-6">
                    <h3 className="text-lg font-interSemiBold text-[#1A202C] mb-4">
                      Patent Image :
                    </h3>
                    <div className="flex flex-col items-center">
                      <div className="flex flex-col md:flex-row gap-6 w-full border border-gray-200 rounded-md shadow-sm p-4">
                        {/* Left side - Image */}
                        <div className="flex-1 flex flex-col items-center">
                          <img
                            src={patentImage.patent_figure_path}
                            alt="Patent Figure"
                            className="max-w-full"
                          />
                        </div>

                        {/* Right side - Legends */}
                        {patentImage.figure_legend &&
                          patentImage.figure_legend.length > 0 && (
                            <div className="flex-1 pl-6 flex items-center justify-center">
                              <div className="grid grid-cols-1 gap-1">
                                {patentImage.figure_legend.map(
                                  (legend: any, index: number) => {
                                    const key = Object.keys(legend)[0];
                                    const value = legend[key];
                                    return (
                                      <div
                                        key={index}
                                        className="flex items-start"
                                      >
                                        <span className="text-sm font-interMedium text-gray-800 mr-2">
                                          {key}.
                                        </span>
                                        <span className="text-sm text-gray-600">
                                          {value}
                                        </span>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Figure number centered below both image and legends */}
                      {patentImage.figure_number && (
                        <p className="mt-4 text-sm text-gray-600 font-interMedium">
                          {patentImage.figure_number.replace(/_/g, ". ")}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>

    <ReAnalyzeModal
      isShow={showReAnalyzeModal}
      setIsShow={setShowReAnalyzeModal}
      vijilizeId={vijilizeId}
      targetPatentId={(() => {
        const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
        if (claimDataStr) {
          try {
            const claimData = JSON.parse(claimDataStr);
            return claimData.activeIndex?.patentId;
          } catch (e) {
            return undefined;
          }
        }
        return undefined;
      })()}
      projectId={id}
      onReanalyze={async (instruction, newImageUrl) => {
        try {
          // Get claim data from sessionStorage first
          const claimDataStr = sessionStorage.getItem("vijilizeClaimData");
          if (!claimDataStr) {
            toast.error("Missing claim data. Please try again.");
            return;
          }

          const claimData = JSON.parse(claimDataStr);
          const { activeIndex, selectedClaim, selectedSimilarity } = claimData;

          // Get team ID from localStorage
          const selectedTeam: any = JSON.parse(
            localStorage.getItem("selectedTeam") as string
          );
          const teamId: string = selectedTeam?.currentTeamId;

          if (!id || !teamId) {
            toast.error("Missing project or team information.");
            return;
          }

          // Get the actual comparison ID
          // Priority: claimData > actualComparisonId state > vijilizeId from URL
          let comparisonIdToUse = vijilizeId;
          
          console.log("=== Comparison ID Resolution ===");
          console.log("vijilizeId from URL params:", vijilizeId);
          console.log("actualComparisonId from state:", actualComparisonId);
          console.log("claimData:", claimData);
          
          // Try to get the comparison ID from claimData if available
          if (claimData.comparisonId) {
            comparisonIdToUse = claimData.comparisonId;
            console.log("Using comparisonId from claimData:", comparisonIdToUse);
          } else if (actualComparisonId) {
            comparisonIdToUse = actualComparisonId;
            console.log("Using actualComparisonId from state:", comparisonIdToUse);
          } else {
            console.log("Using vijilizeId from URL params:", comparisonIdToUse);
          }
          
          console.log("Final comparison ID for status update:", comparisonIdToUse);
          console.log("================================");

          // Show toast and navigate immediately (don't wait for status update)
          toast.success("Reanalysis in progress… please wait a moment.");
          
          // Navigate to Vijilizations tab immediately with the vijilization ID to update
          navigate(`/project/${id}/find-covered-product`, {
            state: {
              reanalysisStarted: true,
              activeSection: 1, // Set to Vijilizations tab
              forceRefresh: true,
              timestamp: Date.now(),
              optimisticStatusUpdate: {
                vijilizationId: comparisonIdToUse,
                status: "in_progress"
              }
            },
          });

          // Update vijilization status to "in_progress" in the background (fire and forget)
          if (comparisonIdToUse) {
            updateVijilizationStatus(comparisonIdToUse, "in_progress").catch(error => {
              console.error("Error updating vijilization status:", error);
              // Status update failed, but navigation already happened
            });
          }

          // Continue reanalysis in background (fire and forget)
          // We wrap this in a self-executing async function to not block navigation
          (async () => {
            try {
              setIsVijilizing(true);

          // Extract claim_preamble text
          let claimPreambleText = "";
          if (selectedClaim?.claim_preamble || selectedClaim?.preamble) {
            const preambleData = selectedClaim?.claim_preamble || selectedClaim?.preamble;
            if (Array.isArray(preambleData)) {
              claimPreambleText = preambleData.map((p: any) => p.description || p).join(" ");
            } else {
              claimPreambleText = preambleData;
            }
          }

          // Extract sourcePatentId from patent_figure_path
          let sourcePatentId = "";
          if (patentImage?.patent_figure_path) {
            // Extract from URL: https://bucket.s3.region.amazonaws.com/sourcePatentId/projectId/figures/...
            const pathMatch = patentImage.patent_figure_path.match(/\/([^\/]+)\/([^\/]+)\/figures\//);
            if (pathMatch && pathMatch[1]) {
              sourcePatentId = pathMatch[1];
            }
          }

          // Fallback to activeIndex.patentId if extraction fails
          if (!sourcePatentId) {
            sourcePatentId = activeIndex.patentId;
          }

          // Extract targetPatentId from targetPatentFigureS3Path
          let targetPatentId = activeIndex.patentId; // Default fallback
          const targetImagePath = newImageUrl || originalImagePath || "";
          
          if (targetImagePath) {
            // Try to extract from URL pattern: .../targetPatentId/projectId/file/...
            // or from presigned URL: https://bucket.s3.region.amazonaws.com/targetPatentId/projectId/file/...
            const targetMatch = targetImagePath.match(/\/([^\/]+)\/([^\/]+)\/file\//);
            if (targetMatch && targetMatch[1]) {
              targetPatentId = targetMatch[1];
            }
          }

          // Get claim element text (the specific feature that was clicked) - try multiple sources
          let claimElement = "";
          if (selectedSimilarity?.description) {
            claimElement = selectedSimilarity.description;
          } else if (selectedClaim?.description) {
            claimElement = selectedClaim.description;
          } else if (fetchedMarkdownContentData?.markdownContent || fetchedMarkdownContentData?.markdown_content) {
            // Try to extract from markdown content
            const markdownText = fetchedMarkdownContentData?.markdownContent || fetchedMarkdownContentData?.markdown_content;
            
            // Method 1: Extract from "#### Feature:" section
            let featureMatch = markdownText.match(/####\s*Feature:\s*\n(.+?)(?:\n\n|\n\||\||$)/s);
            if (featureMatch && featureMatch[1]) {
              claimElement = featureMatch[1].trim();
            } else {
              // Method 2: Extract from heading (##) 
              const headingMatch = markdownText.match(/##\s*(.+?)(?:\n|$)/);
              if (headingMatch && headingMatch[1]) {
                claimElement = headingMatch[1].trim();
              } else {
                // Method 3: Extract first non-empty paragraph after any heading
                const paragraphMatch = markdownText.match(/(?:^|\n)([A-Z][^\n]+(?:design|claim|element|feature)[^\n]+)(?:\n|$)/i);
                if (paragraphMatch && paragraphMatch[1]) {
                  claimElement = paragraphMatch[1].trim();
                }
              }
            }
          }

          // Use the claimElement as the claimFeature (this is the specific feature clicked, not all features)
          const claimFeatureText = claimElement;

          console.log("Debug - Extracted data:", {
            sourcePatentId,
            targetPatentId,
            claimElement,
            claimPreambleText,
            claimFeatureText,
            patentImage,
            selectedClaim,
            selectedSimilarity,
            targetImagePath,
          });

          // Prepare the payload for reanalysis
          const payload = {
            sourcePatentId: sourcePatentId,
            targetPatentId: targetPatentId,
            claimElement: claimElement,
            sourcePatentFigureS3Path: patentImage?.patent_figure_path || "",
            targetPatentFigureS3Path: newImageUrl || originalImagePath || "",
            projectId: id,
            teamId: teamId,
            instruction: instruction || undefined,
            claimPreamble: claimPreambleText,
            claimFeature: claimFeatureText,
          };

          console.log("Reanalyze payload:", payload);

          // Call the reanalysis API
          const response = await reanalyzeSingleClaimElement(payload);
          
          console.log("Reanalysis response:", response);
          console.log("Reanalysis response.image_coordinates:", response?.image_coordinates);
          console.log("Reanalysis response.result:", response?.result);
          console.log("Reanalysis response.result?.image_coordinates:", response?.result?.image_coordinates);
          
          // Save the reanalysis result to the database first
          if (response) {
            // Handle response structure - check if data is nested in 'result' property
            const resultData = response.result || response;
            
            const storedClaimKey = claimData.storedClaimKey || `claim${activeIndex.claimIndex + 1}`;
            
            // Use annotation_id from response if available, otherwise use the one from claimData
            const annotationIdToUse = resultData.annotation_id || response.annotation_id || claimData.annotationId || "";
            
            console.log("Using annotation ID:", annotationIdToUse);
            console.log("Response annotation_id:", response.annotation_id);
            console.log("ClaimData annotation_id:", claimData.annotationId);
            console.log("Result data image_coordinates:", resultData.image_coordinates);
            
            // STEP 1: Call update-citations-for-single-comparison API to get updated analysis
            // We only need to replace the analysis field, keep everything else from resultData
            try {
              console.log("Calling update-citations-for-single-comparison API...");
              console.log("Original analysis:", resultData.analysis || response.analysis);
              
              const citationsPayload = {
                index_name: teamId,
                result: {
                  analysis: resultData.analysis || response.analysis || "",
                  is_match: resultData.is_match !== undefined ? resultData.is_match : response.is_match,
                  match_percentage: resultData.match_percentage !== undefined ? resultData.match_percentage : response.match_percentage,
                  markdown_content: resultData.markdown_content || response.markdown_content || "",
                  markdown_image: resultData.original_image_path || response.original_image_path || "",
                  original_image_path: resultData.original_image_path || response.original_image_path || "",
                  image_coordinates: resultData.image_coordinates || response.image_coordinates || [],
                  annotation_id: annotationIdToUse,
                  is_system_generated: resultData.is_system_generated !== undefined ? resultData.is_system_generated : response.is_system_generated,
                  patent_figure_path: resultData.patent_figure_path || response.patent_figure_path || "",
                  figure_legend: resultData.figure_legend || response.figure_legend || [],
                  figure_number: resultData.figure_number || response.figure_number || "",
                  features: resultData.features || response.features || [],
                  citations: resultData.citations || response.citations || [],
                  total_cost: resultData.total_cost !== undefined ? resultData.total_cost : response.total_cost,
                  hex_code: resultData.hex_code || response.hex_code || "#FF0000",
                },
                team_id: teamId,
                vijilize_id: comparisonIdToUse || "",
              };
              
              const citationsResponse = await updateCitationsForSingleComparison(citationsPayload);
              
              console.log("Citations API response:", citationsResponse);
              
              // ONLY replace analysis and citations from the response, keep everything else from resultData
              if (citationsResponse && citationsResponse.updated_analysis) {
                resultData.analysis = citationsResponse.updated_analysis;
                console.log("✅ Replaced analysis with updated_analysis from citations API");
                console.log("Updated analysis:", resultData.analysis);
              }
              
              if (citationsResponse && citationsResponse.new_citations) {
                resultData.citations = citationsResponse.new_citations;
                console.log("✅ Replaced citations with new_citations from citations API");
                console.log("New citations count:", resultData.citations.length);
                console.log("Footer map:", citationsResponse.footer_map);
              }
            } catch (citationsError) {
              console.error("Error calling update-citations-for-single-comparison:", citationsError);
              // Continue with original analysis if citations API fails
              console.log("Continuing with original analysis due to citations API error");
            }
            
            // STEP 2: Call updateReanalysisResult with resultData (which now has updated analysis)
            await updateReanalysisResult({
              annotationId: annotationIdToUse,
              comparisonId: comparisonIdToUse || "",
              patentId: activeIndex.patentId,
              claimKey: storedClaimKey,
              isMatchedItem: claimData.isMatchedItem,
              markdownContent: resultData.markdown_content || response.markdown_content,
              analysis: resultData.analysis || response.analysis || "",
              originalImagePath: resultData.original_image_path || response.original_image_path,
              patentFigurePath: resultData.patent_figure_path || response.patent_figure_path,
              figureNumber: resultData.figure_number || response.figure_number,
              isMatch: resultData.is_match !== undefined ? resultData.is_match : response.is_match,
              matchPercentage: resultData.match_percentage !== undefined ? resultData.match_percentage : response.match_percentage,
              citations: resultData.citations || response.citations,
              features: resultData.features || response.features,
              figureLegend: resultData.figure_legend || response.figure_legend,
              hexCode: resultData.hex_code || response.hex_code,
              isSystemGenerated: resultData.is_system_generated !== undefined ? resultData.is_system_generated : response.is_system_generated,
              totalCost: resultData.total_cost !== undefined ? resultData.total_cost : response.total_cost,
              imageCoordinates: resultData.image_coordinates || response.image_coordinates,
              projectId: id,
              teamId: teamId,
            });
            
            // Reanalysis completed successfully
            console.log("Reanalysis completed successfully");
            
            // Update status back to completed (backend should handle this, but we do it as backup)
            if (comparisonIdToUse) {
              try {
                await updateVijilizationStatus(comparisonIdToUse, "completed");
              } catch (error) {
                console.error("Error updating vijilization status to completed:", error);
              }
            }
            
            // Trigger a page refresh to show the updated status
            // Navigate to the same page with a refresh flag
            navigate(`/project/${id}/find-covered-product`, {
              state: {
                reanalysisComplete: true,
                activeSection: 1, // Vijilizations tab
                forceRefresh: true,
                timestamp: Date.now(),
                optimisticStatusUpdate: {
                  vijilizationId: comparisonIdToUse,
                  status: "completed"
                }
              },
              replace: true, // Replace current history entry
            });
          }
        } catch (error: any) {
          console.error("Error during background reanalysis:", error);
          // Update status to failed if there's an error
          if (comparisonIdToUse) {
            try {
              await updateVijilizationStatus(comparisonIdToUse, "failed");
            } catch (statusError) {
              console.error("Error updating vijilization status to failed:", statusError);
            }
          }
        } finally {
          setIsVijilizing(false);
        }
      })(); // Execute the async function immediately

        } catch (error: any) {
          console.error("Error initiating reanalysis:", error);
          toast.error(error?.message || "Failed to start reanalysis. Please try again.");
        }
      }}
    />
    
    {isVijilizing && (
      <div className="fixed h-screen inset-0 bg-black bg-opacity-80 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white p-8 rounded-lg shadow-lg flex flex-col items-center space-y-4">
          <img
            src={loginLogo}
            alt="Generating..."
            className="w-32 h-auto animate-pulse"
          />
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-800 mb-1">
              Vijilizing...
            </h3>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default MarkdownViewer;
